import chunk from 'lodash/chunk';

import { DataFieldType } from '~/enums/DataFieldType';
import { pilot } from '~/services/serviceInstances';
import { DataField } from '~/types/Field';
import { PilotId } from '~/types/PilotId';

interface Request {
  ids?: PilotId[];
  isSmart?: boolean;
  limit?: number;
  search?: string;
}

interface Response {
  count: number;
  next: string;
  results: {
    id: PilotId;
    label: string;
    type: DataFieldType | null;
    uf_logical_type?: string;
    uf_field_id?: string | null;
    is_smart: boolean;
    help_text: string;
  }[];
}

const FIELD_IDS_CHUNK_SIZE = 100;

const DATA_FIELD_TYPE_SET: ReadonlySet<string> = new Set(
  Object.values(DataFieldType),
);

const FIELD_TYPE_ALIASES: Readonly<Record<string, DataFieldType>> = {
  TEXT: DataFieldType.STRING,
};

const resolveDataFieldType = (
  field: Response['results'][number],
): DataFieldType | undefined => {
  if (field.type) return field.type;
  if (!field.uf_logical_type) return undefined;

  const normalized = field.uf_logical_type.toUpperCase();
  if (DATA_FIELD_TYPE_SET.has(normalized)) {
    return normalized as DataFieldType;
  }
  return FIELD_TYPE_ALIASES[normalized];
};

const mapResponse = (response: Response): DataField[] => {
  const results: DataField[] = [];

  for (const field of response.results) {
    const type = resolveDataFieldType(field);
    if (!type) continue;
    results.push({
      id: field.id,
      helpText: field.help_text,
      isSmart: field.is_smart,
      label: field.label,
      type,
      ...(field.uf_field_id ? { ufFieldId: field.uf_field_id } : {}),
    });
  }

  return results;
};

const fetchFields = async ({
  ids,
  isSmart,
  limit,
  search,
}: Request): Promise<{
  count: number;
  results: DataField[];
}> => {
  const response: Response = await pilot.get('/field/', {
    params: {
      ids: ids?.join(','),
      is_smart: isSmart,
      limit,
      search,
    },
  });

  const results = mapResponse(response);

  return {
    count: results.length,
    results,
  };
};

export const getFields = async (
  request: Request,
): Promise<{
  count: number;
  results: DataField[];
}> => {
  const { ids, ...rest } = request;

  if (!ids?.length || ids.length <= FIELD_IDS_CHUNK_SIZE) {
    return fetchFields(request);
  }

  const responses = await Promise.all(
    chunk([...new Set(ids)], FIELD_IDS_CHUNK_SIZE).map((chunkIds) =>
      fetchFields({ ...rest, ids: chunkIds }),
    ),
  );

  const results = responses.flatMap((response) => response.results);

  return {
    count: results.length,
    results,
  };
};
